import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import DownArrow from "../../assets/svgs/down-arrow.svg";
import Plus from "../../assets/svgs/Plus.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";

import ActionsMenu from "../../components/ActionsMenu";
import BarData from "../../components/Charts/BarData";
import CircleChart from "../../components/Charts/CircleChart";
import ContractModal from "../../components/ConfirmationModals/ContractModal";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import LeadDataContainer from "../../pages/Leads/components/LeadDataContainer";
import { BASE_URL } from "../../utils/Element";
import { initialExecutedFilterData } from "../../utils/initialData";
import FellOutContract from "../UnderContract/components/FellOutContract";
import EditExecutedModal from "./component/EditExecutedModal";
import ExecutedAddModal from "./component/ExecutedAddModal";
import ExecutedFilter from "./component/ExecutedFilter";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const ExecutedLOI = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);

  // ==================== States ====================
  const [loading, setLoading] = useState(true);
  const [leadsData, setLeadsData] = useState([]);
  const [excecutedDashboard, setExcecutedDashboard] = useState(null);

  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedRows, setExpandedRows] = useState({});

  // Pagination State - RESTORED
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  });

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showfelloutcontract, setShowFellOutContract] = useState(false);

  // Filters - All preserved
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialExecutedFilterData;
  });

  const [filterStatus, setFilterStatus] = useState({
    avg_commission_per_transaction_ytd: false,
    executedlo_buyer: false,
    executedlo_seller: false,
    executedloi_gcl_to_company_ytd: false,
    executedloi_volume_ytd: false,
    executedloi_ytd: false,
    new_executedloi_quarter: false,
    jan: false, feb: false, mar: false, apr: false, may: false,
    jun: false, jul: false, aug: false, sep: false, oct: false,
    nov: false, dec: false,
  });

  // Search with debounce
  const queryParams = new URLSearchParams(location.search);
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  // ==================== Dropdown View ====================
  const viewOptions = [
    { value: 0, label: "My Dashboard" },
    { value: 1, label: "My Executed LOI" },
    { value: 2, label: "Team Dashboard" },
    { value: 3, label: "Team Executed LOI" },
  ];

  const [activeView, setActiveView] = useState(3); // Default: Team Executed LOI

  // ==================== Effects ====================
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  // Main fetch effect
  useEffect(() => {
    if (activeView === 1 || activeView === 3) {
      fetchExecuted(filterData);
    } else {
      fetchDashboardData();
    }
  }, [activeView, currentPage, itemPerPage, search, sortBy, sortDirection]);

  useEffect(() => {
    if (activeView === 0 || activeView === 2) {
      fetchDashboardData();
    }
  }, [activeView]);

  // ==================== API Functions ====================
  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}&search=${search}`;

    const {
      created_at, property_name, noi, created_end_date, created_start_date,
      min_contract_price, max_contract_price, min_contract_cap_rate, max_contract_cap_rate,
      min_asking_price, max_asking_price, max_potential_gcl, broker
    } = filters;

    if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`;
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`;
    if (property_name) params += `&property_name=${encodeURIComponent(property_name)}`;
    if (noi) params += `&noi=${encodeURIComponent(noi)}`;
    if (broker) params += `&broker=${encodeURIComponent(broker)}`;

    if (min_contract_price) params += `&min_contract_price=${encodeURIComponent(min_contract_price)}`;
    if (max_contract_price) params += `&max_contract_price=${encodeURIComponent(max_contract_price)}`;
    if (min_contract_cap_rate) params += `&min_contract_cap_rate=${encodeURIComponent(min_contract_cap_rate)}`;
    if (max_contract_cap_rate) params += `&max_contract_cap_rate=${encodeURIComponent(max_contract_cap_rate)}`;
    if (min_asking_price) params += `&min_asking_price=${encodeURIComponent(min_asking_price)}`;
    if (max_asking_price) params += `&max_asking_price=${encodeURIComponent(max_asking_price)}`;
    if (max_potential_gcl) params += `&max_potential_gcl=true`;

    // All filterStatus (Nothing removed)
    Object.entries(filterStatus).forEach(([key, value]) => {
      if (value) params += `&${key}=true`;
    });

    if (sortBy !== "id") {
      params += `&sort_by=${sortBy}&sort_direction=${sortDirection}`;
    }

    return params;
  };

  const fetchExecuted = (filters = filterData) => {
    setLoading(true);
    const queryParams = buildQueryParams(filters);

    axios
      .get(`${BASE_URL}/executeds?avtive_tab=${activeView}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.executed || {};
        setLeadsData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchDashboardData = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/dashboard-executedloi?active_tab=${activeView}`, config)
      .then((res) => {
        setExcecutedDashboard(res?.data?.executedloi_dashboard);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      setFilterData(filters);
      fetchExecuted(filters);
    } else {
      fetchExecuted(filterData);
    }
  };

  // ==================== Handlers ====================
  const handleViewChange = (e) => {
    const newView = parseInt(e.target.value);
    setActiveView(newView);
    setCurrentPage(1);
    setExpandedRows({});

    // Reset filterStatus only when switching between Dashboard and List view
    if ((activeView <= 1 && newView >= 2) || (activeView >= 2 && newView <= 1)) {
      setFilterStatus({
        avg_commission_per_transaction_ytd: false,
        executedlo_buyer: false,
        executedlo_seller: false,
        executedloi_gcl_to_company_ytd: false,
        executedloi_volume_ytd: false,
        executedloi_ytd: false,
        new_executedloi_quarter: false,
        jan: false, feb: false, mar: false, apr: false, may: false,
        jun: false, jul: false, aug: false, sep: false, oct: false,
        nov: false, dec: false,
      });
    }
  };

  const handleBoxClick = (nameOrKey) => {
    const targetView = activeView === 0 ? 1 : 3;
    setActiveView(targetView);
    setCurrentPage(1);

    setFilterStatus({
      avg_commission_per_transaction_ytd: nameOrKey === "avg_commission_per_transaction_ytd",
      executedlo_buyer: nameOrKey === "executedlo_buyer",
      executedlo_seller: nameOrKey === "executedlo_seller",
      executedloi_gcl_to_company_ytd: nameOrKey === "executedloi_gcl_to_company_ytd",
      executedloi_volume_ytd: nameOrKey === "executedloi_volume_ytd",
      executedloi_ytd: nameOrKey === "executedloi_ytd",
      new_executedloi_quarter: nameOrKey === "new_executedloi_quarter",
      jan: nameOrKey === "jan",
      feb: nameOrKey === "feb",
      mar: nameOrKey === "mar",
      apr: nameOrKey === "apr",
      may: nameOrKey === "may",
      jun: nameOrKey === "jun",
      jul: nameOrKey === "jul",
      aug: nameOrKey === "aug",
      sep: nameOrKey === "sep",
      oct: nameOrKey === "oct",
      nov: nameOrKey === "nov",
      dec: nameOrKey === "dec",
    });
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // ==================== JSX ====================
  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">Executed LOI</p>

        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center w-full md:w-auto flex-wrap">
            {/* Dropdown - Team Executed LOI is Default */}
            <div className="relative min-w-[240px]">
              <select
                value={activeView}
                onChange={handleViewChange}
                className="w-full bg-white rounded-lg px-4 py-2.5 body-S dark-M cursor-pointer appearance-none"
              >
                {viewOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                <img src={DownArrow} alt="down" className="w-4 h-4" />
              </div>
            </div>

            <ExecutedFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              from="Executed LOI"
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts relative flex-1 min-w-[200px]">
              <input
                type="text"
                className="body-S w-full"
                placeholder="Search contact name"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>

            <button onClick={() => setShowAddModal(true)} className="add-contact-button green-bg-H light-L body-S whitespace-nowrap">
              <img className="mr-2" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      {/* ==================== LIST VIEWS (My / Team Executed LOI) ==================== */}
      {(activeView === 1 || activeView === 3) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : leadsData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No Executed LOI Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th><p>Details</p></th>
                    <th className="green-H">
                      <div className="table-header">
                        EXECUTED LOI DATE
                        <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        PROPERTY NAME
                        <img role="button" src={sortBy !== "broker" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("broker"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                       <th>
                      <div className="table-header">
                        Acquisition
                        <img
                          role="button"
                          src={sortBy !== "acquistion" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("acquistion");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Disposition
                        <img
                          role="button"
                          src={sortBy !== "disposition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("disposition");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                      <th>
                      <div className="table-header">
                         Lease Type
                        <img
                          role="button"
                          src={sortBy !== "lease_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("lease_type");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                     <th>
                      <div className="table-header">
                         Lease Term Remaining
                        <img
                          role="button"
                          src={sortBy !== "lease_remaining" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("lease_remaining");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        NOI
                        <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        CONTRACT PRICE
                        <img role="button" src={sortBy !== "contract_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        CONTRACT CAP RATE
                        <img role="button" src={sortBy !== "contract_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_cap_rate"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        % OF ASKING PRICE
                        <img role="button" src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("asking_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        GCI TO COMPANY
                        <img role="button" src={sortBy !== "company_gcl" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("company_gcl"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th><div className="table-header">Actions</div></th>
                  </tr>
                </thead>
                <tbody>
                  {leadsData?.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="dark-H body-N">
                        <td>
                          <img role="button" src={expandedRows[el.id] ? UpArrow : DownArrow} alt="icon" className="sidebar-icons" onClick={() => toggleRowDetails(el.id)} />
                        </td>
                        <td>{moment(el?.created_at).tz('America/New_York').format("MM/DD/YY")}</td>
                        <td className="head-5 capitalize">
                          <Link
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (el?.portfolio === 0) navigate(`/property/${el?.property_id}`);
                              else if (el?.link?.owner_info_type === "contact") navigate(`/contact/${el?.link?.owner?.id}`);
                              else if (el?.link?.owner_info_type === "company") navigate(`/company/${el?.link?.owner?.id}`);
                            }}
                          >
                            {el?.link?.property_name} <br />
                            {el?.link?.street_address} <br />
                            {el?.link?.state && el?.link?.city ? ` ${el?.link?.city} , ${el?.link?.state} ` : ""}
                          </Link>
                        </td>
                            <td>
                          <Link
                                      to={
                             el?.offer?.acquistion_name_id?.type === "contact"
                               ? `/contact/${el?.offer?.acquistion_name_id?.id}`
                               : el?.offer?.acquistion_name_id?.type === "property"
                                 ? `/property/${el?.offer?.acquistion_name_id?.id}`
                                 : "#"
                           }
                           className="body-N dark-H"
                           onClick={(e) => {
                             e.preventDefault();
                             const acquisition = el?.offer?.acquistion_name_id;
                             if (acquisition?.type === "contact") {
                               navigate(`/contact/${acquisition?.id}`);
                             } else if (acquisition?.type === "property") {
                               navigate(`/property/${acquisition?.id}`);
                             }
                           }}
                         >
                           {el?.offer?.acquistion_name || ""}
                          </Link>
                        </td>
                        <td>  
                          <Link
                                      to={
                               el?.offer?.disposition_name_id?.type === "contact"
                                 ? `/contact/${el?.offer?.disposition_name_id?.id}`
                                 : el?.offer?.disposition_name_id?.type === "property"
                                   ? `/property/${el?.offer?.disposition_name_id?.id}`
                                   : "#"
                             }
                             className="body-N dark-H"
                             onClick={(e) => {
                               e.preventDefault();
                               const disposition = el?.offer?.disposition_name_id;
                               if (disposition?.type === "contact") {
                                 navigate(`/contact/${disposition?.id}`);
                               } else if (disposition?.type === "property") {
                                 navigate(`/property/${disposition?.id}`);
                               }
                             }}
                           >
                             {el?.offer?.disposition_name || ""}
                           </Link>
                        </td>
                          <td className="head-5 capitalize">
                            {el?.lease_type} 
                        </td>
                           <td className="head-5 capitalize">
                            {el?.llease_term_remaining} 
                        </td>
                        <td>${el?.link?.noi_format}</td>
                        <td className="text-center">${el?.contract_price_format}</td>
                        <td className="text-center">{el?.contract_cap_rate_format}%</td>
                        <td className="text-center">{el?.asking_price_format_percentage}%</td>
                        <td className="text-center">${el?.gross_commission?.gci_to_company}</td>
                        <td className="flex gap-3 items-center justify-center">
                          <ActionsMenu
                            from="ExecutedLoi"
                            handleEdit={() => { setShowEditModal(true); setSelectedId(el?.id); }}
                            handleDelete={() => { setSelectedId(el?.id); setShowDeleteModal(true); }}
                            showOtherOption={true}
                            otherOptionTitle={"New Counter Offer"}
                            handleOtherOption={() => { setShowAddModal(true); setSelectedId(el?.id); }}
                            otherUnderContractTitle={<> Convert to <br /> Under Contract</>}
                            handleUnderContract={() => { setSelectedId(el?.id); setShowContractModal(true); }}
                            felloutcontractoption={<> Fell Out Of <br /> Executed LOI</>}
                            handlefelloutcontractOption={() => { setShowFellOutContract(true); setSelectedId(el?.id); }}
                          />
                        </td>
                      </tr>


                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">

                                {/* --- TOP INFO SECTION --- */}
                                <div className="grid grid-cols-5 gap-6">

                                <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.seller_broker?.length > 0
                                        ? el.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.offer.seller_broker.length - 1 ? ", " : ""}
                                          </span>
                                        ))
                                        : ""}
                                    </p>
                                  </div>
    

                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.seller_bds?.first_name} {el?.offer?.seller_bds?.last_name || ""}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.buyer_broker?.length > 0
                                        ? el.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.offer.buyer_broker.length - 1 && <br />}
                                          </span>
                                        ))
                                        : ""}
                                    </p>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.offer?.buyer_bds?.first_name} {el?.offer?.buyer_bds?.last_name || ""}
                                    </p>
                                  </div>

                                </div>

                                {/* --- GROSS COMMISSION SECTION --- */}
                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="grid grid-cols-4 gap-6">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>





                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {leadsData?.length > 0 && (
            <ContactPagination
              activeTab={activeView}
              paginationData={paginationData}
              handleNext={handleNext}
              handlePrev={handlePrev}
              onSuccess={onSuccess}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      )}

      {/* ==================== DASHBOARD VIEWS ==================== */}
      {(activeView === 0 || activeView === 2) && (
        <>
          <div className="w-auto">
            <div className="flex items-center gap-3 flex-wrap">
              <LeadDataContainer text="NEW EXECUTED LOI THIS QUARTER" number={excecutedDashboard?.new_executedloi_quarter} onClick={() => handleBoxClick("new_executedloi_quarter")} className="flex-1" />
              <LeadDataContainer text="TOTAL CURRENT EXECUTED LOI YTD" number={excecutedDashboard?.executedloi_ytd} onClick={() => handleBoxClick("executedloi_ytd")} className="flex-1" />
              <LeadDataContainer text="EXECUTED LOI TOTAL VOLUME YTD" number={excecutedDashboard?.executedloi_volume_ytd ? `$${excecutedDashboard.executedloi_volume_ytd}` : '0'} onClick={() => handleBoxClick("executedloi_volume_ytd")} className="flex-1" />
              <LeadDataContainer text="CURRENT POTENTIAL GCI TO COMPANY YTD" number={excecutedDashboard?.executedloi_gcl_to_company_ytd ? `$${excecutedDashboard.executedloi_gcl_to_company_ytd}` : '0'} onClick={() => handleBoxClick("executedloi_gcl_to_company_ytd")} className="flex-1" />
              <LeadDataContainer text="AVG. COMMISSION PER TRANSACTION YTD" number={excecutedDashboard?.avg_commission_per_transaction_ytd ? `$${excecutedDashboard.avg_commission_per_transaction_ytd}` : '0'} onClick={() => handleBoxClick("avg_commission_per_transaction_ytd")} className="flex-1" />
            </div>

            <div className="flex gap-3 mt-4">
              <div className="w-[25%]">
                <LeadDataContainer text="EXECUTED LOI BUYER TRANSACTIONS YTD" number={excecutedDashboard?.executedlo_buyer} onClick={() => handleBoxClick("executedlo_buyer")} className="flex-1" />
                <LeadDataContainer text="EXECUTED LOI SELLER TRANSACTIONS YTD" number={excecutedDashboard?.executedlo_seller} onClick={() => handleBoxClick("executedlo_seller")} className="flex-1" />
              </div>

              <div className="flex gap-3 items-center w-[75%]">
                <CircleChart Title="DAYS SINCE EXECUTED LOI" TotalTitle="Total Excecuted LOI" Total={excecutedDashboard?.total_executedloi} data={excecutedDashboard?.day_graph} />
                <BarData className="w-[50%]" Title="MONTHLY GAIN IN POTENTIAL GCI" BarGraphData={[
                  { name: "Jan", count: excecutedDashboard?.Jan, key: "jan" },
                  { name: "Feb", count: excecutedDashboard?.Feb, key: "feb" },
                  { name: "Mar", count: excecutedDashboard?.Mar, key: "mar" },
                  { name: "Apr", count: excecutedDashboard?.Apr, key: "apr" },
                  { name: "May", count: excecutedDashboard?.May, key: "may" },
                  { name: "Jun", count: excecutedDashboard?.Jun, key: "jun" },
                  { name: "Jul", count: excecutedDashboard?.Jul, key: "jul" },
                  { name: "Aug", count: excecutedDashboard?.Aug, key: "aug" },
                  { name: "Sep", count: excecutedDashboard?.Sept, key: "sep" },
                  { name: "Oct", count: excecutedDashboard?.Oct, key: "oct" },
                  { name: "Nov", count: excecutedDashboard?.Nov, key: "nov" },
                  { name: "Dec", count: excecutedDashboard?.Dec, key: "dec" },
                ]} onBarClick={handleBoxClick} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <ExecutedAddModal showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />
      <EditExecutedModal showModal={showEditModal} onClose={() => setShowEditModal(false)} id={selectedId} onCallApi={() => { setSelectedId(""); onSuccess(); }} />
      <ContractModal showModal={showContractModal} onClose={() => setShowContractModal(false)} id={selectedId} handleUnderContract={() => { /* your handleUnderContract logic */ }} />
      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={() => { /* your delete logic */ }} />
      <FellOutContract from="executedloi" showModal={showfelloutcontract} id={selectedId} onClose={() => setShowFellOutContract(false)} onCallApi={onSuccess} />
    </BaseLayout>
  );
};

export default ExecutedLOI;